// Argus — DashboardPage: overview agregat multi-agent.
// Bloc cleanup-batch-stream-dashboard 2026-05-09.

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import DevicesIcon from '@mui/icons-material/Devices';
import UpdateIcon from '@mui/icons-material/Update';
import { fetchAgents, type Agent } from '../lib/api';
import { dayjs } from '../lib/dayjsSetup';

interface StatCardProps {
  label: string;
  value: number | string;
  color?: 'primary' | 'success' | 'error' | 'warning' | 'info';
  icon?: JSX.Element;
}

function StatCard({ label, value, color = 'primary', icon }: StatCardProps): JSX.Element {
  return (
    <Card variant="outlined" sx={{ minWidth: 160, flex: 1 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {icon}
          <Box>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h4" color={color}>{value}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function lastSeenBucket(lastHeartbeatAt: string | null): 'now' | 'today' | 'yesterday' | 'week' | 'older' | 'never' {
  if (!lastHeartbeatAt) return 'never';
  const now = dayjs();
  const ts = dayjs(lastHeartbeatAt);
  const diffMin = now.diff(ts, 'minute');
  if (diffMin < 5) return 'now';
  const diffH = now.diff(ts, 'hour');
  if (diffH < 24) return 'today';
  if (diffH < 48) return 'yesterday';
  const diffDays = now.diff(ts, 'day');
  if (diffDays < 7) return 'week';
  return 'older';
}

type SeenKey = 'now' | 'today' | 'yesterday' | 'week' | 'older' | 'never';

export function DashboardPage(): JSX.Element {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const list = await fetchAgents();
        if (!cancelled) {
          setAgents(list);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load agents.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    const id = setInterval(() => { void load(); }, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const stats = useMemo(() => {
    const total = agents.length;
    const online = agents.filter((a) => a.isOnline).length;
    const offline = total - online;
    const versions = new Map<string, number>();
    const seenBuckets: Record<SeenKey, number> = { now: 0, today: 0, yesterday: 0, week: 0, older: 0, never: 0 };
    for (const a of agents) {
      const v = a.agentVersion ?? '(unknown)';
      versions.set(v, (versions.get(v) ?? 0) + 1);
      seenBuckets[lastSeenBucket(a.lastHeartbeatAt)]++;
    }
    const versionsList = Array.from(versions.entries()).sort((x, y) => y[1] - x[1]);
    const top = versionsList[0];
    const latestVersion = top !== undefined ? top[0] : '-';
    const onLatest = top !== undefined ? top[1] : 0;
    return { total, online, offline, versions: versionsList, seenBuckets, latestVersion, onLatest };
  }, [agents]);

  if (loading && agents.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, overflow: 'auto', height: '100%' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>Dashboard agregat</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Total stații" value={stats.total} icon={<DevicesIcon color="primary" fontSize="large" />} />
        <StatCard label="Online" value={stats.online} color="success" icon={<OnlinePredictionIcon color="success" fontSize="large" />} />
        <StatCard label="Offline" value={stats.offline} color="error" icon={<CloudOffIcon color="error" fontSize="large" />} />
        <StatCard label="Pe ultima versiune" value={`${stats.onLatest} / ${stats.total}`} color="info" icon={<UpdateIcon color="info" fontSize="large" />} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Distribuție versiuni</Typography>
            {stats.versions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Niciun agent.</Typography>
            ) : (
              <Stack spacing={1.5}>
                {stats.versions.map(([version, count]) => {
                  const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
                  return (
                    <Box key={version}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2"><strong>{version}</strong></Typography>
                        <Typography variant="body2" color="text.secondary">{count} ({pct.toFixed(0)}%)</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
                    </Box>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Ultima activitate</Typography>
            <Stack spacing={1.5}>
              {([
                { key: 'now', label: 'Acum (< 5 min)', color: 'success' as const },
                { key: 'today', label: 'Azi', color: 'info' as const },
                { key: 'yesterday', label: 'Ieri', color: 'warning' as const },
                { key: 'week', label: 'Săptămâna asta', color: 'warning' as const },
                { key: 'older', label: 'Mai vechi de 7 zile', color: 'error' as const },
                { key: 'never', label: 'Niciodată', color: 'default' as const },
              ] as const).map(({ key, label, color }) => {
                const count = stats.seenBuckets[key];
                if (count === 0) return null;
                return (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip label={count} color={color} size="small" sx={{ minWidth: 50 }} />
                    <Typography variant="body2">{label}</Typography>
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
